/**
 * The units on the floor: each unit's pose, and how it is drawn. A unit whose
 * species has a loaded sprite sheet (slice 3, `loadSprites`) is a sprite plane
 * (`sprite-layer.ts`, animated by `sprite-anim.ts`) and has no proxy
 * instance (the proxy batches hold only proxies); any other unit keeps its
 * placeholder proxy (lifted from the dungeon-floors prototype's
 * `dungeon-world.js`): a body, a head, a nose that
 * shows the facing. Every unit has a blob shadow disc at Off that writes no
 * depth (gotcha "camera-and-surface-probes"). A fallen proxy lies on its side,
 * rotated about its feet; a fallen sprite plays its `faint` frames instead;
 * neither is ever scaled. A sheet can land after `show`: the unit switches
 * from its proxy on the next frame.
 */
import { type Dir, type StageStat, type Unit, dirOffset, stageTotal } from '@ab/game-core';
import * as THREE from 'three';
import { presentation } from './constants.ts';
import type { ShaderHook } from './material-hooks.ts';
import type { Palette } from './palette.ts';
import { type SpriteAnimState, advance, frameOf, freshAnim, isAnimating, startOneShot } from './sprite-anim.ts';
import { type SpriteDraw, SpriteLayer } from './sprite-layer.ts';
import type { SpriteSheet } from './sprites.ts';

export type UnitPose = {
  readonly id: string;
  readonly side: Unit['side'];
  readonly small: boolean;
  readonly boss: boolean;
  /** The species id (`monster.golem`, `human.brad`): which sprite sheet draws it. */
  readonly species: string;
  /** Game coordinates, fractional while moving. */
  x: number;
  y: number;
  facing: Dir;
  /** 0 standing, 1 lying on its side. */
  fall: number;
  /** A lunge's offset in game coordinates. */
  lungeX: number;
  lungeY: number;
  /** A flash toward this colour, by `flashAmount` (0..1). */
  flash: THREE.Color | null;
  flashAmount: number;
  /** A gesture's offset: `lift` up, `swayX`/`swayY` across the ground in game coordinates (the unit is never scaled). */
  lift: number;
  swayX: number;
  swayY: number;
  /** Holding a charge: a trembling pose until the charge fires or is interrupted. */
  charging: boolean;
  /** 0 drawn as it is, 1 faded into the background (an escape); `gone` stops drawing it. */
  fade: number;
  gone: boolean;
  /** A sprite's base anim: walking while a move's tween runs, asleep, down (fainted). */
  walking: boolean;
  /** A scene walker running (slice 6a): its sprite plays `run`, or `walk` where the sheet has none. Never set in the dungeon. */
  running: boolean;
  /**
   * A scene walker's place in its walk or run cycle, 0 to 1, from the distance it walked (slice 6a; `main.js`:
   * the frame follows the feet, not the clock). Unset in the dungeon, where the cycle runs on the anim clock.
   */
  cycle?: number;
  sleeping: boolean;
  down: boolean;
  /** A sprite's animation clock (kept across a lost context). */
  readonly anim: SpriteAnimState;
  /** Each stat's stage total as last shown (`stage_changed` carries the new total, so its direction is new minus this). */
  readonly stageTotals: Map<StageStat, number>;
};

const u = presentation.unit;
const stats: readonly StageStat[] = ['patk', 'pdef', 'aatk', 'adef', 'spd'];
const m = new THREE.Matrix4();
const node = new THREE.Matrix4();
const local = new THREE.Matrix4();
const q = new THREE.Quaternion();
const v = new THREE.Vector3();
const s = new THREE.Vector3();
const yAxis = new THREE.Vector3(0, 1, 0);
const zAxis = new THREE.Vector3(0, 0, 1);
const tint = new THREE.Color();
const zero = new THREE.Matrix4().makeScale(0, 0, 0);

/** The yaw that turns local +Z (the nose) toward a game direction; north (+y) is world -Z. */
export const yawOf = (facing: Dir): number => Math.atan2(dirOffset[facing].x, -dirOffset[facing].y);

/** What the test hook reads of a sprite unit: the anim shown, its drawn facing and flip, the frame index in the anim, and whether that frame still changes. */
export type SpriteState = { readonly anim: string; readonly facing: Dir; readonly flip: boolean; readonly frame: number; readonly animating: boolean };

export class UnitLayer {
  private readonly poses = new Map<string, UnitPose>();
  private readonly sheets = new Map<string, SpriteSheet>();
  private readonly sprites: SpriteLayer;
  private speed = 1;
  /** The sprites' clock (ms of frame time, scaled by speed) and the frame time it last advanced at. */
  private clock = 0;
  private lastTime: number | null = null;
  /** The shadow map should be redrawn on the next frame (a sheet landed). */
  private landed = false;
  private meshes: { body: THREE.InstancedMesh; head: THREE.InstancedMesh; nose: THREE.InstancedMesh; shadow: THREE.InstancedMesh } | null = null;
  private capacity = 0;

  private blobs = true;
  private medium = false;
  /** World units per tile (1 in the dungeon; a scene's metres per tile, `setScale`): every size a unit draws at. */
  private scale = 1;

  constructor(
    private readonly scene: THREE.Scene,
    private box: THREE.BufferGeometry,
    private disc: THREE.BufferGeometry,
    private solid: THREE.Material,
    private shadowMaterial: THREE.Material,
    light: ShaderHook,
  ) {
    this.sprites = new SpriteLayer(scene, light);
  }

  /** New shared geometry, the blob material and the light hook after a lost context (the poses and sheets stay; the meshes are made again on the next update). */
  rebind(box: THREE.BufferGeometry, disc: THREE.BufferGeometry, shadowMaterial: THREE.Material, light: ShaderHook): void {
    this.dispose();
    this.box = box;
    this.disc = disc;
    this.shadowMaterial = shadowMaterial;
    this.sprites.rebind(light);
  }

  /** World units per tile for every size a unit draws at (a scene's `spriteScale` over an M plane's tiles); positions are not scaled. */
  setScale(scale: number): void {
    this.scale = scale;
  }

  /** Whether sprites cast into the shadow map at Medium (the dungeon's do; a scene's draw projected cut-outs instead). */
  setSpriteCasting(casts: boolean): void {
    this.sprites.setCasting(casts);
  }

  /**
   * Adds (or replaces) a unit that is not a game-core unit (a scene's traveler
   * or resident, slice 6a): standing, at (x, y) in world units, of `species`.
   */
  addPose(spec: { readonly id: string; readonly species: string; readonly side: Unit['side']; readonly x: number; readonly y: number; readonly facing: Dir }): UnitPose {
    const pose: UnitPose = {
      id: spec.id,
      side: spec.side,
      small: false,
      boss: false,
      species: spec.species,
      x: spec.x,
      y: spec.y,
      facing: spec.facing,
      fall: 0,
      lungeX: 0,
      lungeY: 0,
      flash: null,
      flashAmount: 0,
      lift: 0,
      swayX: 0,
      swayY: 0,
      charging: false,
      fade: 0,
      gone: false,
      walking: false,
      running: false,
      sleeping: false,
      down: false,
      anim: freshAnim(false, this.now()),
      stageTotals: new Map(),
    };
    this.poses.set(spec.id, pose);
    return pose;
  }

  /** A loaded sheet: units of its species draw as sprites from the next frame. */
  install(sheet: SpriteSheet): void {
    this.sheets.set(sheet.id, sheet);
    this.landed = true;
  }

  /** The sprite programs of the current tier on a staging mesh, for `precompile` (the sprite layer's `staging`). */
  spriteStaging(): { readonly colour: THREE.Scene; readonly depth: THREE.Scene | null } {
    return this.sprites.staging();
  }

  /**
   * The sprites' clock now (ms): the clock at the last frame plus the time since, scaled by speed and clamped
   * like a frame's step, so an anim started between two frames starts at its own start.
   */
  now(): number {
    return this.lastTime === null ? this.clock : this.clock + Math.min(Math.max(0, performance.now() - this.lastTime), presentation.anim.maxFrameMs) * this.speed;
  }

  sheetOf(p: UnitPose): SpriteSheet | undefined {
    return this.sheets.get(p.species);
  }

  setSpeed(speed: number): void {
    // The time so far runs at the old speed.
    if (this.lastTime !== null) {
      this.clock = this.now();
      this.lastTime = performance.now();
    }
    this.speed = speed;
  }

  /** Starts a sprite one-shot (attack, cast, hurt) now; returns the ms to its hit frame, 0 for a proxy, a sheet without it or speed 0. */
  oneShot(p: UnitPose, name: string): number {
    const sheet = this.sheetOf(p);
    return sheet === undefined ? 0 : startOneShot(p, sheet, name, this.now(), this.speed === 0);
  }

  /** How long a sprite's anim lasts in its facing, ms; 0 for a proxy or a sheet without it. */
  animMs(p: UnitPose, name: string): number {
    const sheet = this.sheetOf(p);
    return sheet?.has(name) === true ? sheet.resolve(name, p.facing).run.total : 0;
  }

  /** A sprite unit's anim, drawn facing, flip and frame; null for a proxy or a unit not on the floor. */
  spriteState(id: string): SpriteState | null {
    const p = this.poses.get(id);
    const sheet = p === undefined ? undefined : this.sheetOf(p);
    if (p === undefined || sheet === undefined) return null;
    const now = this.now();
    const { resolved, index } = frameOf(p, sheet, now);
    return { anim: resolved.name, facing: resolved.drawn, flip: resolved.flip, frame: index, animating: isAnimating(p, sheet, now) };
  }

  /**
   * A unit's cut-out now, for a projected shadow (slice 6a): the plane's size and pivot in world units, its frame's
   * rectangle in the atlas and flip, and the atlas texture; a proxy's is its body's box, with no texture.
   */
  cutout(id: string): { readonly width: number; readonly height: number; readonly pivotX: number; readonly pivotY: number; readonly rect: readonly [number, number, number, number]; readonly flip: boolean; readonly texture: THREE.Texture | null } | null {
    const p = this.poses.get(id);
    if (p === undefined) return null;
    const sheet = this.sheetOf(p);
    if (sheet === undefined) {
      const body = p.small ? u.smallBody : u.body;
      const head = p.small ? u.smallHead : u.head;
      const height = (head.center + head.height / 2) * this.scale;
      const width = body.width * this.scale;
      return { width, height, pivotX: width / 2, pivotY: 0, rect: [0, 0, 1, 1], flip: false, texture: null };
    }
    const { resolved, index } = frameOf(p, sheet, this.now());
    const frame = resolved.run.frames[index] ?? 0;
    const rect = [sheet.rects[frame * 4] ?? 0, sheet.rects[frame * 4 + 1] ?? 0, sheet.rects[frame * 4 + 2] ?? 0, sheet.rects[frame * 4 + 3] ?? 0] as const;
    const pivotX = (resolved.flip ? sheet.width - sheet.pivotX : sheet.pivotX) * this.scale;
    return { width: sheet.width * this.scale, height: sheet.height * this.scale, pivotX, pivotY: sheet.pivotY * this.scale, rect, flip: resolved.flip, texture: this.sprites.textureOf(sheet.id) };
  }

  /** The height of a unit's top over its feet, in tiles (a sprite's plane, or the proxy's head). */
  topOf(p: UnitPose): number {
    const sheet = this.sheetOf(p);
    if (sheet !== undefined) return (sheet.height - sheet.pivotY) * this.scale;
    const head = p.small ? u.smallHead : u.head;
    return (p.fall > 0.5 ? u.lieLift * 2 : (head.center + head.height / 2) * (p.boss ? u.bossScale : 1)) * this.scale;
  }

  /** Units drawn as proxies (the Medium tier's sheared cut-out shadows are theirs; a sprite casts its own). */
  proxies(): readonly UnitPose[] {
    return [...this.poses.values()].filter((p) => !this.sheets.has(p.species));
  }

  /** Whether a sprite unit that `shown` accepts plays an anim that moves (a loop of more than one frame, or frames still to come). */
  animating(shown: (p: UnitPose) => boolean): boolean {
    const now = this.now();
    for (const p of this.poses.values()) {
      const sheet = this.sheetOf(p);
      if (sheet !== undefined && !p.gone && shown(p) && isAnimating(p, sheet, now)) return true;
    }
    return false;
  }

  /** Units drawn as sprites and as proxies now (the visible ones), and the atlas texels on the GPU. */
  spriteCounts(visible: (pose: UnitPose) => boolean): { readonly unitsDrawn: number; readonly proxies: number; readonly atlasTexels: number } {
    let unitsDrawn = 0;
    let proxies = 0;
    for (const p of this.poses.values()) {
      if (p.gone || !visible(p)) continue;
      if (this.sheets.has(p.species)) unitsDrawn += 1;
      else proxies += 1;
    }
    return { unitsDrawn, proxies, atlasTexels: this.sprites.atlasTexels() };
  }

  /**
   * The proxies' material, whether blob shadows are drawn (the dungeon's Off; its Medium casts cut-out shadows
   * instead), and whether the tier is Medium (the sprites' materials; by default, no blobs). A scene draws blobs at
   * both tiers (slice 6a).
   */
  setTier(material: THREE.Material, blobs: boolean, medium: boolean = !blobs): void {
    this.solid = material;
    this.blobs = blobs;
    this.medium = medium;
    if (this.meshes === null) return;
    for (const mesh of [this.meshes.body, this.meshes.head, this.meshes.nose]) {
      mesh.material = material;
      mesh.receiveShadow = medium;
    }
    this.meshes.shadow.visible = blobs;
    this.sprites.setTier(medium);
  }

  /** Every unit on the floor, in draw order. */
  all(): readonly UnitPose[] {
    return [...this.poses.values()];
  }

  get(id: string): UnitPose | undefined {
    return this.poses.get(id);
  }

  clear(): void {
    this.poses.clear();
  }

  /** Adds (or replaces) a unit at its tile, standing, or lying when it is down. */
  add(unit: Unit): UnitPose {
    const pose: UnitPose = {
      id: unit.id,
      side: unit.side,
      small: unit.kind === 'monster' && unit.boss === null,
      boss: unit.boss !== null,
      species: unit.monster,
      x: unit.pos.x,
      y: unit.pos.y,
      facing: unit.facing,
      fall: unit.down ? 1 : 0,
      lungeX: 0,
      lungeY: 0,
      flash: null,
      flashAmount: 0,
      lift: 0,
      swayX: 0,
      swayY: 0,
      charging: unit.charging !== null && !unit.down,
      fade: 0,
      gone: false,
      walking: false,
      running: false,
      sleeping: !unit.down && unit.statuses.some((st) => st.status === 'sleep'),
      down: unit.down,
      anim: freshAnim(unit.down, this.now()),
      stageTotals: new Map(stats.map((stat) => [stat, stageTotal(unit.stages, stat)])),
    };
    this.poses.set(unit.id, pose);
    return pose;
  }

  remove(id: string): void {
    this.poses.delete(id);
  }

  /**
   * Writes every unit's matrices and colours. `visible` hides an enemy on an
   * unexplored tile; `elevation` lifts a unit standing on the boss's dais.
   * Returns whether the Medium shadow map should be redrawn (a sprite moved,
   * turned, changed anim, appeared or went, or a sheet landed).
   */
  update(palette: Palette, visible: (pose: UnitPose) => boolean, elevation: (pose: UnitPose) => number, camera: THREE.Camera, time = 0): boolean {
    const list = [...this.poses.values()];
    const meshes = this.ensure(list.length);
    const g = presentation.gesture;
    // The sprites' clock: frame time scaled by speed (speed 0 finishes what does not loop), clamped like the tweens'.
    this.clock += this.lastTime === null ? 0 : Math.min(Math.max(0, time - this.lastTime), presentation.anim.maxFrameMs) * this.speed;
    this.lastTime = time;
    const draws: SpriteDraw[] = [];
    let proxies = 0;
    list.forEach((p, i) => {
      const sheet = this.sheetOf(p);
      // A hidden unit's clock still advances (nothing is drawn for it).
      if (sheet !== undefined) advance(p, sheet, this.clock, this.speed === 0);
      const shown = visible(p) && !p.gone;
      if (sheet !== undefined) draws.push({ pose: p, sheet, shown, elevation: elevation(p) });
      if (!shown) {
        meshes.shadow.setMatrixAt(i, zero);
        return;
      }
      // Proxies fill the first instances of the three proxy batches; with none on screen they draw nothing.
      const at = proxies;
      if (sheet === undefined) proxies += 1;
      const big = (p.boss ? u.bossScale : 1) * this.scale;
      const body = p.small ? u.smallBody : u.body;
      const head = p.small ? u.smallHead : u.head;
      const tremble = p.charging ? Math.sin(time / g.trembleMs) * g.tremble : 0;
      const ox = p.lungeX + p.swayX + tremble;
      const oy = p.lungeY + p.swayY;
      node.makeTranslation(p.x + ox, elevation(p) + p.lift + (p.charging ? g.chargeCrouch : 0), -(p.y + oy)).multiply(local.makeRotationAxis(yAxis, yawOf(p.facing)));
      // The fall pivots at the feet and lifts by half the body's thickness, so a lying unit rests on the floor.
      const fallen = new THREE.Matrix4().makeTranslation(0, u.lieLift * p.fall, 0).multiply(local.makeRotationAxis(zAxis, (p.fall * Math.PI) / 2));
      const put = (mesh: THREE.InstancedMesh, x: number, y: number, z: number, w: number, h: number, d: number): void => {
        m.copy(node).multiply(fallen).multiply(local.compose(v.set(x, y, z), q.identity(), s.set(w * big, h * big, d * big)));
        mesh.setMatrixAt(at, m);
      };
      if (sheet === undefined) {
        put(meshes.body, 0, body.center * big, 0, body.width, body.height, body.depth);
        put(meshes.head, 0, head.center * big, 0, head.width, head.height, head.depth);
        put(meshes.nose, 0, (head.center - u.nose.drop) * big, u.nose.forward * big, u.nose.width, u.nose.height, u.nose.depth);
      }
      // The blob disc at Off: a proxy's footprint, or a sprite's (its cell's width in tiles, never the proxy's size).
      const radiusX = sheet === undefined ? u.shadow.radiusX * big : sheet.width * presentation.sprite.blobWidth * this.scale;
      m.makeTranslation(p.x + ox, elevation(p) + u.shadow.y, -(p.y + oy)).multiply(local.makeScale(radiusX, 1, (radiusX * u.shadow.radiusZ) / u.shadow.radiusX));
      meshes.shadow.setMatrixAt(i, m);
      const color = (base: THREE.Color): THREE.Color => {
        tint.copy(base);
        if (p.flash) tint.lerp(p.flash, p.flashAmount);
        return p.fade > 0 ? tint.lerp(palette.background, p.fade) : tint;
      };
      meshes.shadow.setColorAt(i, palette.background);
      if (sheet !== undefined) return;
      meshes.body.setColorAt(at, color(p.boss ? palette.boss : p.side === 'party' ? palette.party : palette.enemy));
      meshes.head.setColorAt(at, color(p.side === 'party' ? palette.exit : palette.trim));
      meshes.nose.setColorAt(at, color(palette.background));
    });
    for (const mesh of [meshes.body, meshes.head, meshes.nose, meshes.shadow]) {
      // An instanced mesh of count 0 issues no draw call (and still holds its buffers, so a rebuild matches).
      mesh.count = mesh === meshes.shadow ? list.length : proxies;
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    }
    const landed = this.landed;
    this.landed = false;
    return this.sprites.update(draws, camera, palette, time, this.clock, this.scale) || landed;
  }

  dispose(): void {
    this.sprites.dispose();
    this.lastTime = null;
    if (this.meshes === null) return;
    for (const mesh of Object.values(this.meshes)) {
      mesh.removeFromParent();
      mesh.dispose();
    }
    this.meshes = null;
    this.capacity = 0;
  }

  /** The four instanced meshes, rebuilt with room to spare when the floor holds more units than they fit. */
  private ensure(count: number): NonNullable<UnitLayer['meshes']> {
    if (this.meshes !== null && count <= this.capacity) return this.meshes;
    this.dispose();
    const capacity = Math.max(u.initialCapacity, count * 2);
    const make = (name: string, geometry: THREE.BufferGeometry, material: THREE.Material): THREE.InstancedMesh => {
      const mesh = new THREE.InstancedMesh(geometry, material, capacity);
      mesh.name = `units-${name}`;
      mesh.frustumCulled = false;
      mesh.setColorAt(0, tint.set(0, 0, 0));
      this.scene.add(mesh);
      return mesh;
    };
    this.meshes = {
      body: make('body', this.box, this.solid),
      head: make('head', this.box, this.solid),
      nose: make('nose', this.box, this.solid),
      shadow: make('shadow', this.disc, this.shadowMaterial),
    };
    this.capacity = capacity;
    this.setTier(this.solid, this.blobs, this.medium);
    return this.meshes;
  }
}
